import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import notify from '../../../utils/notify';
import '../../TenderOpening/components/TenderOpening.css';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';

const ACCOUNT_OPTIONS = [
  { label: 'Has account', value: 'true' },
  { label: 'No account', value: 'false' },
];

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fetchJson = async (url, token) => {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};

const IdamanUserView = () => {
  const token = getAuthToken();
  // positionId bisa datang dari halaman Position ID IDAMAN (?positionId=...).
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [positionOptions, setPositionOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [positionFilter, setPositionFilter] = useState(
    searchParams.get('positionId') || ''
  );
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchFilterOptions = useCallback(async () => {
    if (!token) return;
    try {
      const [positions, departments] = await Promise.all([
        fetchJson(
          `${api.API_URL}/position-idaman?PageNumber=1&PageSize=1000&SortBy=positionId`,
          token
        ),
        fetchJson(`${api.API_URL}/position-idaman/departments`, token),
      ]);
      setPositionOptions(
        (positions.data?.items || []).map(p => ({
          value: p.positionId,
          label: p.name ? `${p.positionId} · ${p.name}` : p.positionId,
        }))
      );
      setDepartmentOptions(
        (departments.data || []).map(d => ({ label: d, value: d }))
      );
    } catch (error) {
      notify.apiError(error, 'load the filters');
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const qs = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }
      if (positionFilter) qs.append('PositionId', positionFilter);
      if (departmentFilter) qs.append('Department', departmentFilter);
      if (accountFilter) qs.append('HasAccount', accountFilter);

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const result = await fetchJson(
        `${api.API_URL}/position-idaman/users?${qs.toString()}`,
        token
      );
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    positionFilter,
    departmentFilter,
    accountFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePositionFilterChange = value => {
    setPositionFilter(value);
    setCurrentPage(1);
    // Jaga URL tetap sesuai filter supaya link bisa dibagikan/di-refresh.
    const next = new URLSearchParams(searchParams);
    if (value) next.set('positionId', value);
    else next.delete('positionId');
    setSearchParams(next, { replace: true });
  };

  const handleDepartmentFilterChange = value => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  const handleAccountFilterChange = value => {
    setAccountFilter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setDebouncedSearchText('');
    handlePositionFilterChange('');
    setDepartmentFilter('');
    setAccountFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const positionLabel =
    positionOptions.find(o => o.value === positionFilter)?.label ||
    positionFilter;

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    positionFilter && {
      key: 'position',
      label: 'Position',
      value: positionLabel,
      onRemove: () => handlePositionFilterChange(''),
    },
    departmentFilter && {
      key: 'department',
      label: 'Department',
      value: departmentFilter,
      onRemove: () => handleDepartmentFilterChange(''),
    },
    accountFilter && {
      key: 'account',
      label: 'Account',
      value: ACCOUNT_OPTIONS.find(o => o.value === accountFilter)?.label,
      onRemove: () => handleAccountFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      id: 'displayName',
      name: 'Name',
      selector: row => row.displayName,
      sortable: true,
      minWidth: '220px',
      cell: row => (
        <div className='py-1'>
          <span className='fw-medium d-block text-break'>
            {row.displayName || '-'}
          </span>
          <span className='text-muted small text-break'>
            {[row.employeeNumber, row.jobTitle].filter(Boolean).join(' · ') ||
              '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'email',
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      minWidth: '220px',
      cell: row => <span className='text-break'>{row.email || '-'}</span>,
    },
    {
      id: 'positionId',
      name: 'Position',
      selector: row => row.positionId,
      sortable: true,
      minWidth: '220px',
      cell: row => (
        <div className='py-1'>
          <code>{row.positionId || '-'}</code>
          <span className='text-muted small d-block text-break'>
            {row.isPositionRegistered
              ? row.positionName || '-'
              : 'Position ID not registered'}
          </span>
        </div>
      ),
    },
    {
      id: 'department',
      name: 'Department',
      selector: row => row.department,
      sortable: true,
      minWidth: '180px',
      cell: row => <span>{row.department || '-'}</span>,
    },
    {
      name: 'Account / Role',
      minWidth: '200px',
      cell: row =>
        row.appUserId ? (
          <div className='d-flex flex-wrap gap-1'>
            {row.roles?.length > 0 ? (
              row.roles.map(role => (
                <span key={role} className='to-tag'>
                  {role}
                </span>
              ))
            ) : (
              <span className='text-muted'>No role</span>
            )}
          </div>
        ) : (
          <span className='to-status to-status--muted'>No account</span>
        ),
    },
    {
      id: 'lastSyncedDate',
      name: 'Last Synced',
      selector: row => row.lastSyncedDate,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className='text-muted small'>
          {formatDate(row.lastSyncedDate)}
        </span>
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '90px',
      center: true,
      ignoreRowClick: true,
      cell: row =>
        row.appUserId ? (
          <Link
            to={`/apps-usermanagement/dtl/${row.appUserId}`}
            className='btn btn-sm btn-ghost-secondary btn-icon hy-icon-action'
            title='Open user account to manage roles'
            aria-label='Open user account'
          >
            <i className='ri-user-settings-line' aria-hidden='true' />
          </Link>
        ) : (
          <span className='text-muted'>-</span>
        ),
    },
  ];

  return (
    <React.Fragment>
      <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
        <div className='email-content minimal-border'>
          <div className='p-4'>
            <div className='to-hero mb-4'>
              <PageBreadcrumb fallback={{ label: 'IDAMAN Users' }} />

              <div className='d-flex align-items-center gap-3 flex-wrap'>
                <span className='to-hero-icon'>
                  <i className='ri-group-line' aria-hidden='true' />
                </span>
                <div className='flex-grow-1'>
                  <h4 className='to-hero-title'>IDAMAN Users</h4>
                  <p className='to-hero-subtitle'>
                    IDAMAN users pulled by the daily sync. Open a user account
                    to change its role from General.
                  </p>
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <Link to='/apps-position-id/view' className='to-hero-btn'>
                    <i className='ri-profile-line' aria-hidden='true' />
                    Position IDs
                  </Link>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={() => fetchData()}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className='to-panel'>
              <div className='to-toolbar'>
                <ListSearch
                  value={searchText}
                  onChange={handleSearch}
                  placeholder='Search name, email, employee no., or job title...'
                  ariaLabel='Search IDAMAN users'
                />
                <ListFilterSelect
                  label='Position'
                  value={positionFilter}
                  onChange={handlePositionFilterChange}
                  allLabel='All Positions'
                  options={positionOptions}
                />
                <ListFilterSelect
                  label='Department'
                  value={departmentFilter}
                  onChange={handleDepartmentFilterChange}
                  allLabel='All Departments'
                  options={departmentOptions}
                />
                <ListFilterSelect
                  label='Account'
                  value={accountFilter}
                  onChange={handleAccountFilterChange}
                  allLabel='All'
                  options={ACCOUNT_OPTIONS}
                />
                <ClearFiltersButton
                  count={filterChips.length}
                  onClick={clearFilters}
                />
                <span className='to-toolbar-meta'>
                  {isLoading
                    ? 'Loading...'
                    : `Showing ${startEntry}–${endEntry} of ${totalRows} users`}
                </span>
              </div>

              <ActiveFilterChips chips={filterChips} />

              <div className='to-table-wrap'>
                <DataTable
                  columns={columns}
                  data={data.items}
                  onSort={(column, dir) => {
                    setSortField(column.id);
                    setSortDirection(dir);
                    setCurrentPage(1);
                  }}
                  sortServer
                  highlightOnHover
                  persistTableHead
                  defaultSortFieldId='displayName'
                  defaultSortAsc
                  progressPending={isLoading}
                  progressComponent={<SkeletonRows />}
                  noDataComponent={
                    <EmptyState
                      title='No IDAMAN user found'
                      emptyHint='No IDAMAN user has been pulled yet. Register Position IDs, then run Sync Now on the Position ID IDAMAN page or wait for the daily sync at 05:00 WIB.'
                      filteredHint={`No IDAMAN user matches ${filterChips
                        .map(chip => `${chip.label} "${chip.value}"`)
                        .join(
                          ', '
                        )}. Try a different keyword or clear the filters.`}
                      hasFilters={hasFilters}
                      onClear={clearFilters}
                    />
                  }
                  customStyles={{
                    headCells: {
                      style: { paddingLeft: '12px', paddingRight: '12px' },
                    },
                    cells: {
                      style: {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        wordBreak: 'break-word',
                      },
                    },
                    table: { style: { width: '100%' } },
                  }}
                />
              </div>

              <ListPager
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePerRowsChange}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default IdamanUserView;
